/**
 * useVetProState — État global du mode Pro Vétérinaire.
 *
 * Symétrique à useAppState mais isolé : sa propre persistence localStorage
 * (clé 'ekynair:vetProState'), aucune interaction avec useAppState côté
 * propriétaire. Permet une bascule UI complètement étanche entre les deux
 * modes.
 *
 * Schéma v3 (refonte nav 2026-05-28) :
 *   - vetProPage : 'home' | 'horses' | 'newCase' | 'caseDetail' | 'sessionView'
 *                | 'horseSessions' | 'horseSession' | 'sessions' | 'sessionGlobal'
 *                | 'account' | 'feedback' — défaut 'sessions' (sprint 2026-05-29,
 *                supersede D-NAV-SESS-1). Au mount, une entrée "nue" (onglet de
 *                1er niveau) retombe sur 'sessions' via coerceLanding ; une page
 *                profonde avec son entité (caseDetail/sessionView/sessionGlobal/
 *                horseSessions/horseSession) est RESPECTÉE (deep-link refresh).
 *     - 'sessionView'    (Sprint C.1) : vue lecture seule d'une session dans
 *       le contexte d'un VetCase (VetCaseSessionView). Consomme selectedSessionId
 *       + selectedCaseId.
 *     - 'horseSessions'  (refonte 2026-05-28) : liste des sessions d'un horse
 *       hors contexte VetCase (VetHorseSessionsView). Consomme selectedHorseId.
 *     - 'horseSession'   (refonte 2026-05-28) : vue lecture seule d'une session
 *       hors contexte VetCase (VetHorseSessionView). Consomme selectedHorseId
 *       + selectedSessionId.
 *     - 'sessions'       (sprint feature/vetpro-nav-sessions-global) : liste
 *       GLOBALE des sessions du véto (SessionListPage iso-propriétaire, filtre
 *       cheval visible, édition complète). Aucun param requis.
 *     - 'sessionGlobal'  (sprint feature/vetpro-nav-sessions-global) : vue
 *       d'une session ouverte depuis la liste globale (SessionDashboard
 *       complet, édition activée — asymétrique avec 'sessionView' lecture
 *       seule, car véto = owner V1.0). Consomme selectedSessionId.
 *
 *   ⚠️ Le hook n'enforce AUCUNE whitelist sur la string vetProPage : on élargit
 *   les vetProPage acceptés ici sans bump SCHEMA_VERSION (pas de migration v4
 *   nécessaire — l'état v3 stocké reste valide). Voir D-NAV-SESS-2.
 *   - selectedCaseId    : string | null — défaut null (consommé caseDetail / sessionView).
 *   - selectedSessionId : string | null — défaut null (consommé sessionView / horseSession).
 *   - selectedHorseId   : string | null — défaut null (consommé horseSessions / horseSession).
 *   - handleVetProNavigate(page, opts?) : setter avec persistence (spread opts).
 *
 * Conventions :
 *   - localStorage clé : 'ekynair:vetProState'.
 *   - Format stocké : JSON { vetProPage, selectedCaseId, selectedSessionId, selectedHorseId, schemaVersion: 3 }.
 *
 * Migration v2 → v3 (douce, refonte 2026-05-28) :
 *   - Si parsed.schemaVersion === 2 : on réécrit en v3 avec selectedHorseId: null
 *     + reset selectedCaseId/selectedSessionId à null + vetProPage='home'. C'est
 *     une perte ACCEPTABLE du dernier état nav (cohérent D-C1-6 v1→v2). On
 *     réécrit immédiatement pour éviter les bumps de cleanup ultérieurs.
 *   - Si schemaVersion absent ou autre : reset complet DEFAULT_STATE (comportement
 *     historique conservé).
 *
 * Voir spec V1.0 §6.1, §6.5. Lot 4a + Sprint C.1 + refonte nav 2026-05-28.
 */
import { useState, useEffect, useCallback } from 'react';

export const STORAGE_KEY = 'ekynair:vetProState';
const SCHEMA_VERSION = 3;
// Atterrissage Sessions (sprint 2026-05-29, supersede D-NAV-SESS-1 qui fixait
// 'home'/Mes cas comme défaut). Cohérent avec l'owner (PAGES.LIST). Le reset
// dur au login vit dans useAuthState.handleLogin (removeItem STORAGE_KEY).
const DEFAULT_STATE = {
  vetProPage: 'sessions',
  selectedCaseId: null,
  selectedSessionId: null,
  selectedHorseId: null,
};

// Pages "profondes" : elles consomment une entité sélectionnée et constituent
// l'équivalent d'un deep-link (il n'existe AUCUN deep-link URL dans cette SPA
// mono-URL — toute la nav profonde est état localStorage). Au refresh sur "/",
// on RESPECTE ces pages si leur entité est présente ; toute autre page (onglet
// de 1er niveau, entrée "nue") retombe sur l'atterrissage Sessions. Le garde
// par page évite de restaurer une vue-entité orpheline (id null → écran vide).
// Voir §AVIS CC sprint 2026-05-29.
const DEEP_PAGE_GUARDS = {
  caseDetail:    (s) => !!s.selectedCaseId,
  sessionView:   (s) => !!s.selectedSessionId,
  sessionGlobal: (s) => !!s.selectedSessionId,
  horseSessions: (s) => !!s.selectedHorseId,
  horseSession:  (s) => !!s.selectedSessionId,
};

// Redirige l'entrée "nue" vers Sessions, préserve une page profonde valide.
function coerceLanding(state) {
  const guard = DEEP_PAGE_GUARDS[state.vetProPage];
  if (guard && guard(state)) return state;
  return { ...state, vetProPage: 'sessions' };
}

function readInitialState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    if (!parsed) return { ...DEFAULT_STATE };

    if (parsed.schemaVersion === SCHEMA_VERSION) {
      return coerceLanding({
        vetProPage: parsed.vetProPage || DEFAULT_STATE.vetProPage,
        selectedCaseId: parsed.selectedCaseId || null,
        selectedSessionId: parsed.selectedSessionId || null,
        selectedHorseId: parsed.selectedHorseId || null,
      });
    }

    // Migration douce v2 → v3 : on conserve la clé localStorage mais on
    // reset l'état nav. La réécriture en v3 se fait via le useEffect ci-dessous
    // au prochain render (le state local part de DEFAULT_STATE).
    if (parsed.schemaVersion === 2) {
      return { ...DEFAULT_STATE };
    }

    // Autres cas (v1, parsing échec, schemaVersion inconnu) : reset complet.
    return { ...DEFAULT_STATE };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function useVetProState() {
  const [state, setState] = useState(readInitialState);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...state, schemaVersion: SCHEMA_VERSION }),
      );
    } catch {
      // Quota dépassé ou private mode — silencieux, l'état reste en mémoire.
    }
  }, [state]);

  const handleVetProNavigate = useCallback((page, opts = {}) => {
    setState((prev) => ({ ...prev, vetProPage: page, ...opts }));
  }, []);

  return {
    vetProPage: state.vetProPage,
    selectedCaseId: state.selectedCaseId,
    selectedSessionId: state.selectedSessionId,
    selectedHorseId: state.selectedHorseId,
    handleVetProNavigate,
  };
}
